import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Briefcase, Crown, Plane, Save, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { DatePicker, TimePicker, combineDateTime } from "../../lib/date-pickers";
import { Input, Labeled } from "../Rentals";
import { PhoneField, PreferredContactMethodField } from "../../components/PhoneField";
import { CurrencyInput } from "../../components/CurrencyInput";
import { PaydayEnvBadge } from "../../components/PaydayEnvBadge";
import { buildPhoneFieldValue, getPhoneFieldError, touchPhoneField } from "../../lib/phone";
import { formatISK, parseCurrencyInput } from "../../lib/currency";

const SERVICE_OPTIONS = [
  { value: "airport_transfer", label: "Airport Transfer", icon: Plane },
  { value: "private_driver", label: "Private Driver", icon: Briefcase },
  { value: "day_tour", label: "Private Day Tour", icon: Sparkles },
];

function buildAdminQuotePayload(serviceKind, form) {
  return {
    service_kind: serviceKind,
    start_at: combineDateTime(form.date, form.time).toISOString(),
    pricing_tier: serviceKind === "private_driver" ? "direct" : form.pricing_tier,
    transfer_direction: serviceKind === "airport_transfer" ? form.transfer_direction : null,
    requested_hours: serviceKind === "private_driver" ? Number(form.requested_hours) || null : null,
    day_tour_hours: serviceKind === "day_tour" ? Number(form.day_tour_hours) || null : null,
    pickup_at_kef: serviceKind === "airport_transfer" ? form.transfer_direction === "arrival" : !!form.pickup_at_kef,
    extra_stop_count: Number(form.extra_stop_count) || 0,
    commission_percent: form.pricing_tier === "b2b" ? Number(form.commission_percent) || null : null,
  };
}

function adminBookingEndAt(serviceKind, start, form) {
  if (serviceKind === "private_driver") return new Date(start.getTime() + (Number(form.requested_hours) || 3) * 60 * 60 * 1000);
  if (serviceKind === "day_tour") return new Date(start.getTime() + (Number(form.day_tour_hours) || 8) * 60 * 60 * 1000);
  return new Date(start.getTime() + 2 * 60 * 60 * 1000);
}

function quoteHighlights(quote) {
  if (!quote) return [];
  const items = [quote.service_label];
  if (quote.night_transfer) items.push("Late-night start");
  if (quote.same_day) items.push("Same-day request");
  if (quote.pickup_at_kef) items.push("KEF pickup");
  if (quote.extra_stop_count) items.push(`${quote.extra_stop_count} extra ${quote.extra_stop_count === 1 ? "stop" : "stops"}`);
  return items;
}

export default function AdminTransportPricing() {
  const today = new Date();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [bookingSaving, setBookingSaving] = useState(false);
  const [serviceKind, setServiceKind] = useState("airport_transfer");
  const [quote, setQuote] = useState(null);
  const [paydayConfig, setPaydayConfig] = useState(null);
  const [form, setForm] = useState({
    date: today,
    time: "10:00",
    transfer_direction: "arrival",
    requested_hours: 3,
    day_tour_hours: 8,
    pickup_at_kef: false,
    extra_stop_count: 0,
    passengers: 2,
    invoice_mode: "b2b",
    pricing_tier: "b2b",
    commission_percent: 20,
    customer_name: "",
    company_name: "",
    kennitala: "",
    billing_address: "",
    billing_zip_code: "",
    billing_city: "",
    billing_country: "Iceland",
    email: "",
    phone: buildPhoneFieldValue(),
    preferred_contact_method: null,
    notes: "",
  });

  const load = async () => {
    const [{ data }, payday] = await Promise.all([api.get("/admin/transport-pricing"), api.get("/admin/payday/config")]);
    setSettings(data);
    setPaydayConfig(payday.data);
    setForm((prev) => ({ ...prev, commission_percent: data.b2b_commission_default_percent ?? 20 }));
  };

  useEffect(() => {
    load().catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post("/admin/transport/pricing-quote", buildAdminQuotePayload(serviceKind, form));
        setQuote(data);
      } catch {
        setQuote(null);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [serviceKind, form]);

  useEffect(() => {
    setForm((prev) => {
      const next = { ...prev };
      if (serviceKind === "private_driver") {
        next.pricing_tier = "direct";
      } else if (prev.invoice_mode === "b2b" && prev.pricing_tier !== "b2b") {
        next.pricing_tier = "b2b";
      } else if (prev.invoice_mode !== "b2b" && prev.pricing_tier === "b2b") {
        next.pricing_tier = "direct";
      }
      return JSON.stringify(next) === JSON.stringify(prev) ? prev : next;
    });
  }, [serviceKind, form.invoice_mode]);

  const saveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...settings,
        direct_airport_transfer_isk: parseCurrencyInput(settings.direct_airport_transfer_isk) || 0,
        direct_hourly_driver_isk: parseCurrencyInput(settings.direct_hourly_driver_isk) || 0,
        direct_hourly_driver_min_hours: Number(settings.direct_hourly_driver_min_hours),
        direct_day_tour_8h_isk: parseCurrencyInput(settings.direct_day_tour_8h_isk) || 0,
        direct_day_tour_10h_isk: parseCurrencyInput(settings.direct_day_tour_10h_isk) || 0,
        b2b_airport_transfer_isk: parseCurrencyInput(settings.b2b_airport_transfer_isk) || 0,
        b2b_day_tour_8h_isk: parseCurrencyInput(settings.b2b_day_tour_8h_isk) || 0,
        b2b_day_tour_10h_isk: parseCurrencyInput(settings.b2b_day_tour_10h_isk) || 0,
        b2b_commission_min_percent: Number(settings.b2b_commission_min_percent),
        b2b_commission_max_percent: Number(settings.b2b_commission_max_percent),
        b2b_commission_default_percent: settings.b2b_commission_default_percent === "" ? null : Number(settings.b2b_commission_default_percent),
        premium_airport_transfer_isk: parseCurrencyInput(settings.premium_airport_transfer_isk) || 0,
        premium_day_tour_8h_isk: parseCurrencyInput(settings.premium_day_tour_8h_isk) || 0,
        premium_day_tour_10h_isk: parseCurrencyInput(settings.premium_day_tour_10h_isk) || 0,
        night_surcharge_isk: parseCurrencyInput(settings.night_surcharge_isk) || 0,
        same_day_percent: Number(settings.same_day_percent),
        kef_pickup_surcharge_isk: parseCurrencyInput(settings.kef_pickup_surcharge_isk) || 0,
        extra_stop_surcharge_isk: parseCurrencyInput(settings.extra_stop_surcharge_isk) || 0,
      };
      const { data } = await api.put("/admin/transport-pricing", payload);
      setSettings(data);
      setForm((prev) => ({ ...prev, commission_percent: data.b2b_commission_default_percent ?? prev.commission_percent }));
      toast.success("Transport pricing saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const createAdminBooking = async (e) => {
    e.preventDefault();
    const phoneError = getPhoneFieldError(form.phone);
    if (phoneError) {
      setForm({ ...form, phone: touchPhoneField(form.phone) });
      toast.error(phoneError);
      return;
    }
    setBookingSaving(true);
    try {
      const start = combineDateTime(form.date, form.time);
      const end = adminBookingEndAt(serviceKind, start, form);
      await api.post("/admin/bookings", {
        service_type: "transport",
        transport_service_kind: serviceKind,
        invoice_mode: form.invoice_mode,
        pricing_tier: serviceKind === "private_driver" ? "direct" : form.pricing_tier,
        transfer_direction: serviceKind === "airport_transfer" ? form.transfer_direction : null,
        requested_hours: serviceKind === "private_driver" ? Number(form.requested_hours) || null : null,
        day_tour_hours: serviceKind === "day_tour" ? Number(form.day_tour_hours) || null : null,
        pickup_at_kef: serviceKind === "airport_transfer" ? form.transfer_direction === "arrival" : !!form.pickup_at_kef,
        extra_stop_count: Number(form.extra_stop_count) || 0,
        commission_percent: form.pricing_tier === "b2b" ? Number(form.commission_percent) || null : null,
        start_at: start.toISOString(),
        end_at: end.toISOString(),
        customer_name: form.customer_name,
        company_name: form.company_name || null,
        kennitala: form.kennitala || null,
        billing_address: form.billing_address || null,
        billing_zip_code: form.billing_zip_code || null,
        billing_city: form.billing_city || null,
        billing_country: form.billing_country || null,
        email: form.email,
        phone: form.phone.e164 || null,
        phone_country: form.phone.country || null,
        preferred_contact_method: form.preferred_contact_method || null,
        passengers: Number(form.passengers) || 1,
        notes: form.notes || null,
      });
      toast.success("Admin transport booking created");
      setForm((prev) => ({ ...prev, customer_name: "", email: "", phone: buildPhoneFieldValue(), preferred_contact_method: null, notes: "" }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBookingSaving(false);
    }
  };

  const Icon = useMemo(() => SERVICE_OPTIONS.find((service) => service.value === serviceKind)?.icon || Plane, [serviceKind]);
  const pricingHighlights = useMemo(() => quoteHighlights(quote), [quote]);

  if (!settings) {
    return <div data-testid="admin-transport-pricing-loading" className="text-sm text-slate-500">Loading transport pricing…</div>;
  }

  return (
    <div className="space-y-8" data-testid="admin-transport-pricing-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Transport Pricing</h1>
        <p className="text-sm text-slate-500">Manage DIRECT, B2B, and PREMIUM transport pricing in kr, then preview exact totals and create admin-side transport bookings.</p>
        <PaydayEnvBadge config={paydayConfig} testId="admin-transport-pricing-payday-badge" />
      </div>

      <form onSubmit={saveSettings} className="rounded-2xl border border-slate-200 bg-white p-6 space-y-6">
        <div className="grid gap-6 xl:grid-cols-4">
          <PricingBlock title="Direct" fields={[
            ["Airport Transfer", "direct_airport_transfer_isk", "currency"],
            ["Hourly Driver", "direct_hourly_driver_isk", "currency"],
            ["Min hours", "direct_hourly_driver_min_hours", "number"],
            ["Day Tour 8h", "direct_day_tour_8h_isk", "currency"],
            ["Day Tour 10h", "direct_day_tour_10h_isk", "currency"],
          ]} settings={settings} setSettings={setSettings} />
          <PricingBlock title="B2B" fields={[
            ["Airport Transfer", "b2b_airport_transfer_isk", "currency"],
            ["Day Tour 8h", "b2b_day_tour_8h_isk", "currency"],
            ["Day Tour 10h", "b2b_day_tour_10h_isk", "currency"],
            ["Commission min %", "b2b_commission_min_percent", "number"],
            ["Commission max %", "b2b_commission_max_percent", "number"],
            ["Commission default %", "b2b_commission_default_percent", "number"],
          ]} settings={settings} setSettings={setSettings} />
          <PricingBlock title="Premium" fields={[
            ["Airport Transfer", "premium_airport_transfer_isk", "currency"],
            ["Day Tour 8h", "premium_day_tour_8h_isk", "currency"],
            ["Day Tour 10h", "premium_day_tour_10h_isk", "currency"],
            ["Premium label", "premium_label", "text"],
          ]} settings={settings} setSettings={setSettings} />
          <PricingBlock title="Dynamic rules" fields={[
            ["Night surcharge", "night_surcharge_isk", "currency"],
            ["Same-day %", "same_day_percent", "number"],
            ["KEF pickup surcharge", "kef_pickup_surcharge_isk", "currency"],
            ["Extra stop surcharge", "extra_stop_surcharge_isk", "currency"],
          ]} settings={settings} setSettings={setSettings} />
        </div>
        <button type="submit" disabled={saving} data-testid="admin-transport-pricing-save" className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
          <Save size={14} /> {saving ? "Saving…" : "Save transport pricing"}
        </button>
      </form>

      <form onSubmit={createAdminBooking} className="rounded-2xl border border-slate-200 bg-white p-6 space-y-6">
        <div className="flex items-center gap-2 text-sky-700"><Icon size={16} /><p className="text-xs font-bold uppercase tracking-[0.2em]">Admin transport booking creator</p></div>
        <div className="grid gap-3 sm:grid-cols-3" data-testid="admin-transport-service-switcher">
          {SERVICE_OPTIONS.map((service) => {
            const ActiveIcon = service.icon;
            const active = serviceKind === service.value;
            return (
              <button key={service.value} type="button" onClick={() => setServiceKind(service.value)} data-testid={`admin-transport-service-${service.value}`} className={`rounded-xl border px-4 py-3 text-left ${active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-slate-50 text-slate-900"}`}>
                <div className="flex items-center gap-2"><ActiveIcon size={16} /><span className="font-semibold">{service.label}</span></div>
              </button>
            );
          })}
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Labeled label="Date"><DatePicker value={form.date} onChange={(date) => setForm({ ...form, date })} min={today} data-testid="admin-transport-date" /></Labeled>
          <Labeled label="Time"><TimePicker value={form.time} onChange={(time) => setForm({ ...form, time })} data-testid="admin-transport-time" /></Labeled>
          {serviceKind === "airport_transfer" ? (
            <Labeled label="Direction">
              <select data-testid="admin-transport-direction" value={form.transfer_direction} onChange={(e) => setForm({ ...form, transfer_direction: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900">
                <option value="arrival">KEF → Reykjavík</option>
                <option value="departure">Reykjavík → KEF</option>
              </select>
            </Labeled>
          ) : serviceKind === "private_driver" ? (
            <Labeled label="Hours">
              <select data-testid="admin-transport-hours" value={form.requested_hours} onChange={(e) => setForm({ ...form, requested_hours: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900">
                {[3, 4, 5, 6, 8, 10].map((hours) => <option key={hours} value={hours}>{hours} hours</option>)}
              </select>
            </Labeled>
          ) : (
            <Labeled label="Day tour length">
              <select data-testid="admin-transport-day-tour-hours" value={form.day_tour_hours} onChange={(e) => setForm({ ...form, day_tour_hours: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900">
                <option value="8">8 hours</option>
                <option value="10">10 hours</option>
              </select>
            </Labeled>
          )}
          <Labeled label="Passengers"><Input data-testid="admin-transport-passengers" type="number" min="1" max="8" value={form.passengers} onChange={(e) => setForm({ ...form, passengers: e.target.value })} /></Labeled>
          <Labeled label="Invoice mode">
            <select data-testid="admin-transport-invoice-mode" value={form.invoice_mode} onChange={(e) => setForm({ ...form, invoice_mode: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900">
              <option value="b2b">B2B</option>
              <option value="cash_manual">Cash / manual</option>
              <option value="online_card">Online card</option>
            </select>
          </Labeled>
          <Labeled label="Pricing tier">
            <select data-testid="admin-transport-tier" value={serviceKind === "private_driver" ? "direct" : form.pricing_tier} onChange={(e) => setForm({ ...form, pricing_tier: e.target.value })} disabled={serviceKind === "private_driver"} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900 disabled:bg-slate-100">
              <option value="direct">DIRECT</option>
              <option value="b2b">B2B</option>
              {serviceKind !== "private_driver" && <option value="premium">PREMIUM</option>}
            </select>
          </Labeled>
          <Labeled label="Extra stops">
            <select data-testid="admin-transport-extra-stops" value={form.extra_stop_count} onChange={(e) => setForm({ ...form, extra_stop_count: e.target.value })} className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900">
              {[0, 1, 2, 3].map((count) => <option key={count} value={count}>{count}</option>)}
            </select>
          </Labeled>
          {form.pricing_tier === "b2b" && serviceKind !== "private_driver" && (
            <Labeled label="Commission %">
              <Input data-testid="admin-transport-commission" type="number" min={settings.b2b_commission_min_percent} max={settings.b2b_commission_max_percent} value={form.commission_percent} onChange={(e) => setForm({ ...form, commission_percent: e.target.value })} />
            </Labeled>
          )}
        </div>
        {form.pricing_tier === "b2b" && serviceKind !== "private_driver" && (
          <p className="-mt-1 text-xs text-slate-500" data-testid="admin-transport-commission-hint">
            B2B commission must stay between {settings.b2b_commission_min_percent}% and {settings.b2b_commission_max_percent}%.
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Labeled label="Customer name"><Input data-testid="admin-transport-name" required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></Labeled>
          <Labeled label="Company / agency"><Input data-testid="admin-transport-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} /></Labeled>
          <Labeled label="Email"><Input data-testid="admin-transport-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Labeled>
          <Labeled label="Kennitala"><Input data-testid="admin-transport-kennitala" value={form.kennitala} onChange={(e) => setForm({ ...form, kennitala: e.target.value })} /></Labeled>
          <Labeled label="Phone / WhatsApp"><PhoneField value={form.phone} onChange={(phone) => setForm({ ...form, phone })} countryTestId="admin-transport-phone-country" numberTestId="admin-transport-phone" errorTestId="admin-transport-phone-error" /></Labeled>
          <Labeled label="Preferred contact method"><PreferredContactMethodField value={form.preferred_contact_method} onChange={(preferred_contact_method) => setForm({ ...form, preferred_contact_method })} testid="admin-transport-contact-method" /></Labeled>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Labeled label="Billing address"><Input data-testid="admin-transport-billing-address" value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} /></Labeled>
          <Labeled label="ZIP code"><Input data-testid="admin-transport-billing-zip" value={form.billing_zip_code} onChange={(e) => setForm({ ...form, billing_zip_code: e.target.value })} /></Labeled>
          <Labeled label="City"><Input data-testid="admin-transport-billing-city" value={form.billing_city} onChange={(e) => setForm({ ...form, billing_city: e.target.value })} /></Labeled>
          <Labeled label="Country"><Input data-testid="admin-transport-billing-country" value={form.billing_country} onChange={(e) => setForm({ ...form, billing_country: e.target.value })} /></Labeled>
        </div>

        <Labeled label="Internal notes"><textarea rows={3} data-testid="admin-transport-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" /></Labeled>

        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="admin-transport-quote-card">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-sky-700">{quote?.tier_label || "Preview"}</p>
          <p className="mt-2 font-heading text-3xl font-semibold tabular-nums">{quote ? formatISK(quote.fixed_price_isk) : "Calculating…"}</p>
          <div className="mt-2 text-xs text-slate-500" data-testid="admin-transport-invoice-summary">
            Invoice mode: <span className="font-semibold text-slate-700">{form.invoice_mode}</span>
            <span className="mx-2 text-slate-300">·</span>
            VAT: <span className="font-semibold text-slate-700">11%</span>
          </div>
          {pricingHighlights.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="admin-transport-quote-highlights">
              {pricingHighlights.map((item) => (
                <span key={item} className="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-inset ring-slate-200">
                  {item}
                </span>
              ))}
            </div>
          )}
          {quote?.breakdown?.length ? (
            <div className="mt-4 space-y-2 text-sm text-slate-600">
              {quote.breakdown.map((item) => (
                <div key={item.label} className="flex items-center justify-between gap-3">
                  <span>{item.label}</span>
                  <span className="font-semibold text-slate-900">{formatISK(item.amount_isk)}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <button type="submit" disabled={bookingSaving || !quote} data-testid="admin-transport-create-booking" className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
          {bookingSaving ? "Creating…" : "Create transport booking"} <ArrowRight size={14} />
        </button>
      </form>
    </div>
  );
}

function PricingBlock({ title, fields, settings, setSettings }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-sky-700">{title}</p>
      {fields.map(([label, key, type = "number"]) => (
        <Labeled key={key} label={label}>
          {type === "currency" ? (
            <CurrencyInput
              data-testid={`admin-transport-setting-${key}`}
              value={settings[key] ?? ""}
              onChange={(value) => setSettings({ ...settings, [key]: value })}
              className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900"
            />
          ) : (
            <input
              data-testid={`admin-transport-setting-${key}`}
              type={type}
              value={settings[key] ?? ""}
              onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
              className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900"
            />
          )}
        </Labeled>
      ))}
    </div>
  );
}